import { takeLatest, put } from 'redux-saga/effects';
import { setSearchState, appNotify, fetchSearch, fetchSearchLastPricedByMe } from 'store/actions';
import { postWithToken, getWithToken } from 'api/network';
import { Constants } from 'utilities/constants';

// The frontend components/store are module-generic; the only per-module difference is which
// backend the search hits. Post-Acq searches tasks/imports, Acq searches opportunities/imports.
const searchApiBase = module =>
  module === Constants.Module.POSTACQ ? '/api/postAcquisition/search' : '/api/underwrite/search';
const lastPricedPath = module =>
  module === Constants.Module.POSTACQ ? 'getTasksLastPricedByMe' : 'getTxnOppsLastPricedByMe';

export function* searchResultFetchSaga() {
  yield takeLatest(setSearchState, function* ({ payload }) {
    const { module, newState } = payload;
    if (newState?.searchValue === "") {
      // clear search results
      yield put(setSearchState(module, { data: [], resultsLimited: false, importCount: 0, propertyCount: 0, auctionCount: 0 }));
    }
    if (newState?.searchValue && newState.searchValue.length > 0) {
      yield put(fetchSearch(module, newState.searchValue));
    }
  });

  yield takeLatest(fetchSearch, function* ({ payload }) {
    const { module, searchValue } = payload;
    try {
      yield put(setSearchState(module, { loading: true, isSearchingLastPriced: false }));
      let res;
      res = yield postWithToken(searchApiBase(module), searchValue);
      const data = yield res.json();
      yield put(setSearchState(module, { loading: false }));
      let [searchData, importCount, propertyCount, opportunitiesCount, auctionCount, hasMoreData] = processData(data, module);
      yield put(setSearchState(module, { data: searchData, resultsLimited: hasMoreData, importCount, opportunitiesCount, propertyCount, auctionCount }));
    } catch (error) {
      yield put(setSearchState(module, { loading: false }));
      yield put(appNotify(error.message, 'error'));
    }
  });

  yield takeLatest(fetchSearchLastPricedByMe, function* ({ payload: module }) {
    try {
      yield put(setSearchState(module, {
        loading: true,
        isSearchingLastPriced: true,
        searchBoxValue: '',
        searchValue: ''
      }));
      let res;
      res = yield getWithToken(`${searchApiBase(module)}/${lastPricedPath(module)}`);
      const data = yield res.json();
      yield put(setSearchState(module, { loading: false }));

      let [searchData, importCount, propertyCount, opportunitiesCount, auctionCount, hasMoreData] = processData(data, module);
      yield put(setSearchState(module, { data: searchData, resultsLimited: hasMoreData, importCount, opportunitiesCount, propertyCount, auctionCount }));
    } catch (error) {
      yield put(setSearchState(module, { loading: false }));
      yield put(appNotify(error.message, 'error'));
    }
  })
}

function processData(data, module) {
  let newData = [];
  let propertyCount = 0;
  let auctionCount = 0;
  let importCount = 0;
  let hasResultsHidden = false;
  for (let i = 0; i < data.length; i++) {
    let searchResult = {};
    switch (data[i].resultType) {
      case "import":
        searchResult.resultType = data[i].resultType;
        searchResult.id = data[i].id;
        searchResult.channel = data[i].channel;
        searchResult.requestType = data[i].requestType;
        searchResult.title = data[i].importName;
        searchResult.importedBy = data[i].importedBy;
        searchResult.importedOn = new Date(data[i].createdOn);
        searchResult.portfolioName = null;
        // Post-acq imports carry a task count (no opportunities); acq imports carry both.
        searchResult.data = module === Constants.Module.POSTACQ
          ? [`Tasks: ${data[i].importCount}`]
          : [`Imported: ${data[i].importCount}, Opportunities: ${data[i].opportunitiesCount}`];
        if (importCount < 20) {
          importCount++;
          newData.push(searchResult);
        } else {
          hasResultsHidden = true;
        }
        break;
      case "task":
        // Post-acq analog of "property": a task result reuses the propertyCount bucket so the
        // drawer's total count keeps working without a separate task counter.
        searchResult.resultType = data[i].resultType;
        searchResult.id = data[i].id;
        searchResult.userId = data[i].userId;
        searchResult.channel = data[i].channel;
        searchResult.requestType = data[i].requestType;
        searchResult.assetId = data[i].assetId;
        searchResult.title = data[i].fullAddress;
        searchResult.importedBy = data[i].importedBy;
        searchResult.importedOn = new Date(data[i].createdOn);
        searchResult.portfolioName = null;
        searchResult.taskStatus = data[i].taskStatus;
        searchResult.underwriteStatus = data[i].underwriteStatus;
        searchResult.consumedOn = data[i].consumedOn ? new Date(data[i].consumedOn) : null;
        // A task can only be opened once its latest load has completed; the card is disabled otherwise.
        searchResult.postAcquisitionLoadCompletedOn = data[i].postAcquisitionLoadCompletedOn ? new Date(data[i].postAcquisitionLoadCompletedOn) : null;
        searchResult.latestPricingDateTime = data[i].latestPricingDateTime ? new Date(data[i].latestPricingDateTime) : null;
        searchResult.data = [[showData(data[i].beds, " beds"), showData(data[i].baths, " baths"), showData(data[i].yearBuilt, " built"), showData(data[i].sqft, "sf")].join(" ")];
        if (propertyCount < 100) {
          propertyCount++;
          newData.push(searchResult);
        } else {
          hasResultsHidden = true;
        }
        break;
      case "property":
        searchResult.resultType = data[i].resultType;
        searchResult.id = data[i].id;
        searchResult.userId = data[i].userId;
        searchResult.channel = data[i].channel;
        searchResult.title = data[i].fullAddress;
        searchResult.importedBy = data[i].importedBy;
        searchResult.importedOn = new Date(data[i].createdOn);
        searchResult.portfolioName = data[i].portfolioName;
        searchResult.opportunityStatus = data[i].opportunityStatus;
        searchResult.underwriteStatus = data[i].underwriteStatus;
        searchResult.latestPricingDateTime = data[i].latestPricingDateTime ? new Date(data[i].latestPricingDateTime) : null;
        searchResult.data = [[showData(data[i].beds, " beds"), showData(data[i].baths, " baths"), showData(data[i].yearBuilt, " built"), showData(data[i].sqft, "sf")].join(" ")];
        if (propertyCount < 100) {
          propertyCount++;
          newData.push(searchResult);
        } else {
          hasResultsHidden = true;
        }
        break;
      case "auction":
        searchResult.resultType = data[i].resultType;
        searchResult.id = data[i].id;
        searchResult.title = data[i].portfolioName;
        searchResult.importedBy = "";
        searchResult.importedOn = new Date(data[i].createdOn);
        searchResult.completedOn = data[i].completedOn ? new Date(data[i].completedOn) : null;
        searchResult.portfolioName = null;
        searchResult.data = [showData(data[i].portfolioCount, " properties")];
        if (auctionCount < 20) {
          auctionCount++;
          newData.push(searchResult);
        } else {
          hasResultsHidden = true;
        }
        break;
      default:
        break;
    }
  }
  return [newData, importCount, propertyCount, auctionCount, hasResultsHidden];
}

function showData(property, display) {
  if (property) {
    return property + display;
  }
  return "";
}
